import { AxiosResponse } from 'axios';
import { BasicSelectListItem, CustomerInvoiceValidation, SavedCreditCard, AutoPayOption, PaymentPlan, PaymentLimits } from '../types/AllTypesExports';
import BaseNoviApiRepo from './BaseNoviApiRepo';

class PaymentsRepo extends BaseNoviApiRepo {
    getBillableParties(transactionType: string, customerId: number, getMemberTypeDefaultBillableParty: boolean = false) {
        const url = `/novi-api/payments/billable-parties?transactionType=${transactionType}&forCustomerId=${customerId}&getMemberTypeDefaultBillableParty=${getMemberTypeDefaultBillableParty}`;
        const attemptFetch = (): Promise<BasicSelectListItem[]> => {
            return super.get<BasicSelectListItem[]>(url)
                .then(super.getNoviApiDataHandler<BasicSelectListItem[]>());
        };
        return attemptFetch()
            .catch(initialError => {
                // If first attempt doesn't succeed we will retry after 1 second
                return new Promise<BasicSelectListItem[]>((resolve, reject) => {
                    setTimeout(() => {
                        attemptFetch()
                            .then(resolve)
                            .catch(retryError => {
                                // If retry doesn't succeed, we reject with signal for manual retry
                                reject({
                                    manualRetry: true,
                                    message: "Billable party list failed to load. ",
                                    originalError: retryError
                                });
                            });
                    }, 1000);
                });
            });
    } 

    getSalesTerms() {
        return super.get<BasicSelectListItem[]>('/novi-api/payments/sales-terms')
            .then(super.getNoviApiDataHandler<BasicSelectListItem[]>())
            .catch(super.getNoviApiErrorHandler<BasicSelectListItem[]>());
    }

    getDefaultSalesTerm(customerUniqueId?: string | null) {
        const segment = customerUniqueId ? `/${encodeURIComponent(customerUniqueId)}` : '';
        return super.get<{defaultSalesTermId: number}>(`/novi-api/payments/default-sales-term${segment}`)
            .then((axiosResponse: AxiosResponse<{ defaultSalesTermId: number }>) => {
                var data = super.getNoviApiDataHandler<{ defaultSalesTermId: number }>()(axiosResponse);
                return data.defaultSalesTermId;
            })
            .catch(super.getNoviApiErrorHandler<number>());
    }

    getSavedCreditCards() {
        return super.get<SavedCreditCard[]>('/novi-api/payments/credit-cards')
            .then(super.getNoviApiDataHandler<SavedCreditCard[]>())
            .catch(super.getNoviApiErrorHandler<SavedCreditCard[]>());
    }

    getSavedAch() {
        return super.get<SavedCreditCard[]>('/novi-api/payments/credit-cards')
            .then(super.getNoviApiDataHandler<SavedCreditCard[]>())
            .catch(super.getNoviApiErrorHandler<SavedCreditCard[]>());
    }

    getPaymentPlans(eligibleItems?: [], total?: number, eligibleForPaymentPlanAmount?: number, customerID?: number) {
        return super.post<PaymentPlan[]>('/novi-api/payments/payment-plans', {
            EligibleItems: eligibleItems,
            Total: total,
            EligibleForPaymentPlanAmount: eligibleForPaymentPlanAmount,
            CustomerID: customerID,
        })
            .then(super.getNoviApiDataHandler<PaymentPlan[]>())
            .catch(super.getNoviApiErrorHandler<PaymentPlan[]>());
    }

    validateCustomerCanBeInvoiced(customerUniqueId: string) {
        return super.get<CustomerInvoiceValidation>(`/novi-api/payments/validate-customer-can-be-invoiced/${encodeURIComponent(customerUniqueId)}`)
            .then(super.getNoviApiDataHandler<CustomerInvoiceValidation>())
            .catch(super.getNoviApiErrorHandler<CustomerInvoiceValidation>());
    }

    getTaxRate(shippingState?: string, billingState?: string, purchaserUniqueId?: string | null, eventId?: number) {
        const purchaserParam = purchaserUniqueId ? `&purchaserUniqueId=${encodeURIComponent(purchaserUniqueId)}` : '';
        const eventParam = eventId ? `&eventId=${eventId}` : '';
        return super.get<{ taxRate: number }>(`/novi-api/payments/tax-rate/?shippingState=${encodeURIComponent(shippingState ?? '')}&billingState=${encodeURIComponent(billingState ?? '')}${purchaserParam}${eventParam}`)
            .then((axiosResponse: AxiosResponse<{ taxRate: number }>) => {
                var data = super.getNoviApiDataHandler<{ taxRate: number }>()(axiosResponse);
                return data.taxRate;
            })
            .catch(super.getNoviApiErrorHandler<Number>());
    }

    getAutoPayOptions(type?: string) {
        return super.get<{items: AutoPayOption[]}>(`/novi-api/payments/auto-pay-options?type=${type}`)
            .then(super.getNoviApiDataHandler<{ items: AutoPayOption[] }>())
            .catch(super.getNoviApiErrorHandler<{ items: AutoPayOption[] }>());
    }

    getPaymentLimits() {
        return super.get<{ limits: PaymentLimits }>(`/novi-api/payments/payment-limits`)
            .then(super.getNoviApiDataHandler<{ limits: PaymentLimits }>())
            .catch(super.getNoviApiErrorHandler<{ limits: PaymentLimits }>());
    }
}

export const paymentsRepo = new PaymentsRepo();